import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

const STATUS_COLORS = {
  NEW: '#E8920A', PENDING_REVIEW: '#E8920A', ELIGIBLE: '#3B82F6',
  IN_PROGRESS: '#8B5CF6', NEGOTIATING: '#F59E0B', WON: '#0D9F57',
  PAID: '#0D9F57', LOST: '#DC2626', INELIGIBLE: '#6B7280',
  NEEDS_HUMAN: '#DC2626', COMPLETE: '#0D9F57',
};

const STATUS_LABELS = {
  NEW: 'Nouveau', PENDING_REVIEW: 'En attente', ELIGIBLE: 'Eligible',
  IN_PROGRESS: 'En cours', NEGOTIATING: 'Négociation', WON: 'Gagné',
  PAID: 'Payé', LOST: 'Perdu', INELIGIBLE: 'Non éligible',
  NEEDS_HUMAN: 'Action requise', COMPLETE: 'Complet',
};

function timeAgo(ts) {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (mins < 1) return 'À l\'instant';
  if (mins < 60) return `${mins}min`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}j`;
  return new Date(ts).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

function formatTime(ts) {
  return new Date(ts).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit'
  });
}

export default function Inbox() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  const loadConversations = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/inbox`);
      setConversations(r.data);
    } catch (err) {
      console.error('Failed to load inbox:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      const r = await axios.get(`${API}/api/inbox/${convId}/messages`);
      setMessages(r.data);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  }, []);

  useEffect(() => { loadConversations(); }, []);

  useEffect(() => {
    if (selected) loadMessages(selected.id);
  }, [selected]);

  // Auto-refresh every 15s
  useEffect(() => {
    const interval = setInterval(() => {
      loadConversations();
      if (selected) loadMessages(selected.id);
    }, 15000);
    return () => clearInterval(interval);
  }, [selected]);

  const unlock = async (phone) => {
    if (!window.confirm('Débloquer cette conversation ? Le bot reprendra le contrôle.')) return;
    try {
      await axios.post(`${API}/api/conversations/${phone}/unlock`);
      await loadConversations();
      if (selected?.client_phone === phone) {
        setSelected(prev => ({ ...prev, state: 'COMPLETE' }));
      }
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.error || err.message));
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      if (selected.case_id) {
        await axios.post(`${API}/api/cases/${selected.case_id}/message`, { message: reply.trim() });
      } else {
        // No case yet — use followup endpoint (handles 24h window + templates automatically)
        await axios.post(`${API}/api/followup/${selected.client_phone}`, { message: reply.trim() });
      }
      setReply('');
      await loadMessages(selected.id);
      await loadConversations();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.error || err.message));
    } finally {
      setSending(false);
    }
  };

  const filtered = conversations.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.client_name?.toLowerCase().includes(q) ||
           c.client_phone?.includes(q) ||
           c.flight_number?.toLowerCase().includes(q);
  });

  // Group by needs attention first
  const sorted = [...filtered].sort((a, b) => {
    if (a.state === 'NEEDS_HUMAN' && b.state !== 'NEEDS_HUMAN') return -1;
    if (b.state === 'NEEDS_HUMAN' && a.state !== 'NEEDS_HUMAN') return 1;
    return new Date(b.last_message_at) - new Date(a.last_message_at);
  });

  return (
    <div style={styles.container}>
      {/* LEFT — Conversation list */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <h2 style={styles.sidebarTitle}>Messagerie</h2>
          <span style={styles.count}>{conversations.length}</span>
        </div>
        <input
          style={styles.search}
          placeholder="Rechercher..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={styles.convList}>
          {loading ? (
            <p style={styles.empty}>Chargement...</p>
          ) : sorted.length === 0 ? (
            <p style={styles.empty}>Aucune conversation</p>
          ) : sorted.map(conv => (
            <div
              key={conv.id}
              onClick={() => setSelected(conv)}
              style={{
                ...styles.convItem,
                background: selected?.id === conv.id ? '#EEF2FF' : '#fff',
                borderLeft: conv.state === 'NEEDS_HUMAN'
                  ? '3px solid #DC2626'
                  : selected?.id === conv.id
                    ? '3px solid #0D1B3E'
                    : '3px solid transparent',
              }}
            >
              <div style={styles.convAvatar}>
                {(conv.client_name || conv.client_phone || '?')[0].toUpperCase()}
              </div>
              <div style={styles.convInfo}>
                <div style={styles.convTop}>
                  <span style={styles.convName}>
                    {conv.client_name || conv.client_phone}
                    {conv.state === 'NEEDS_HUMAN' && (
                      <span style={styles.alertDot} title="Conversation bloquée — cliquer pour débloquer">●</span>
                    )}
                  </span>
                  <span style={styles.convTime}>{timeAgo(conv.last_message_at)}</span>
                </div>
                <div style={styles.convBottom}>
                  <span style={styles.convFlight}>
                    {conv.flight_number || 'Vol N/A'}
                  </span>
                  {conv.case_status && (
                    <span style={{
                      ...styles.statusPill,
                      background: `${STATUS_COLORS[conv.case_status]}18`,
                      color: STATUS_COLORS[conv.case_status]
                    }}>
                      {STATUS_LABELS[conv.case_status] || conv.case_status}
                    </span>
                  )}
                </div>
                {conv.last_message && (
                  <p style={styles.convPreview}>
                    {conv.last_message_direction === 'outbound' ? '→ ' : ''}
                    {conv.last_message.substring(0, 60)}{conv.last_message.length > 60 ? '...' : ''}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT — Messages */}
      <div style={styles.main}>
        {!selected ? (
          <div style={styles.noConv}>
            <p style={styles.noConvText}>Sélectionnez une conversation</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={styles.chatHeader}>
              <div>
                <div style={styles.chatName}>
                  {selected.client_name || selected.client_phone}
                  {selected.state === 'NEEDS_HUMAN' && (
                    <span style={styles.needsHumanBadge}>Action requise</span>
                  )}
                </div>
                <div style={styles.chatSub}>
                  +{selected.client_phone}
                  {selected.flight_number && ` · Vol ${selected.flight_number}`}
                </div>
              </div>
              <div style={{display:'flex', gap:8}}>
                {selected.state === 'NEEDS_HUMAN' && (
                  <button
                    onClick={() => unlock(selected.client_phone)}
                    style={{...styles.viewCaseBtn, background:'#DC2626'}}
                    title="Remettre le bot en contrôle"
                  >
                    Débloquer
                  </button>
                )}
                {selected.case_id && (
                  <button
                    onClick={() => navigate(`/cases/${selected.case_id}`)}
                    style={styles.viewCaseBtn}
                  >
                    Voir le dossier
                  </button>
                )}
              </div>
            </div>

            {/* Messages */}
            <div style={styles.messages}>
              {messages.length === 0 ? (
                <p style={styles.empty}>Aucun message</p>
              ) : messages.map((msg, i) => {
                const isOut = msg.direction === 'outbound';
                const showDate = i === 0 ||
                  new Date(messages[i-1].created_at).toDateString() !== new Date(msg.created_at).toDateString();
                return (
                  <div key={msg.id}>
                    {showDate && (
                      <div style={styles.dateSep}>
                        {new Date(msg.created_at).toLocaleDateString('fr-FR', {
                          weekday: 'long', day: '2-digit', month: 'long'
                        })}
                      </div>
                    )}
                    <div style={{
                      ...styles.msgRow,
                      justifyContent: isOut ? 'flex-end' : 'flex-start'
                    }}>
                      <div style={{
                        ...styles.bubble,
                        background: isOut ? '#0D1B3E' : '#F3F4F6',
                        color: isOut ? '#fff' : '#1F2937',
                        borderRadius: isOut ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      }}>
                        <p style={styles.bubbleText}>{msg.content}</p>
                        <span style={{
                          ...styles.bubbleTime,
                          color: isOut ? 'rgba(255,255,255,0.6)' : '#9CA3AF'
                        }}>
                          {formatTime(msg.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply */}
            <div style={styles.replyBox}>
              {!selected.case_id && (
                <p style={styles.noCaseNote}>
                  Aucun dossier lié — le message sera envoyé directement au client.
                </p>
              )}
              <textarea
                value={reply}
                onChange={e => setReply(e.target.value)}
                placeholder="Écrire un message au client..."
                style={styles.replyInput}
                onKeyDown={e => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') sendReply();
                }}
              />
              <div style={styles.replyFooter}>
                <span style={styles.replyHint}>Cmd+Entrée pour envoyer</span>
                <button
                  onClick={sendReply}
                  disabled={sending || !reply.trim()}
                  style={styles.sendBtn}
                >
                  {sending ? 'Envoi...' : 'Envoyer'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { display: 'flex', height: 'calc(100vh - 60px)', overflow: 'hidden', background: '#F7F7F8' },
  sidebar: { width: 320, borderRight: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', background: '#fff', flexShrink: 0 },
  sidebarHeader: { padding: '16px 16px 8px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #F3F4F6' },
  sidebarTitle: { fontSize: 16, fontWeight: 800, color: '#0D1B3E', margin: 0, flex: 1 },
  count: { background: '#0D1B3E', color: '#fff', borderRadius: 100, fontSize: 11, fontWeight: 700, padding: '2px 7px' },
  search: { margin: '10px 12px', padding: '8px 12px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, outline: 'none', fontFamily: 'inherit' },
  convList: { flex: 1, overflowY: 'auto' },
  convItem: { display: 'flex', gap: 10, padding: '12px 14px', cursor: 'pointer', borderBottom: '1px solid #F9FAFB', transition: 'background 0.1s' },
  convAvatar: { width: 38, height: 38, borderRadius: '50%', background: '#0D1B3E', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, flexShrink: 0 },
  convInfo: { flex: 1, minWidth: 0 },
  convTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 },
  convName: { fontSize: 13, fontWeight: 700, color: '#0D1B3E', display: 'flex', alignItems: 'center', gap: 4 },
  alertDot: { color: '#DC2626', fontSize: 10 },
  convTime: { fontSize: 11, color: '#9CA3AF', flexShrink: 0 },
  convBottom: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 },
  convFlight: { fontSize: 11, color: '#6B7280', fontWeight: 600 },
  statusPill: { fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 100 },
  convPreview: { fontSize: 12, color: '#9CA3AF', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  main: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  noConv: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  noConvText: { color: '#9CA3AF', fontSize: 14 },
  chatHeader: { padding: '14px 20px', borderBottom: '1px solid #E5E7EB', background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  chatName: { fontSize: 15, fontWeight: 800, color: '#0D1B3E', display: 'flex', alignItems: 'center', gap: 8 },
  needsHumanBadge: { fontSize: 11, fontWeight: 700, background: '#FEE2E2', color: '#DC2626', padding: '2px 8px', borderRadius: 100 },
  chatSub: { fontSize: 12, color: '#9CA3AF', marginTop: 2 },
  viewCaseBtn: { background: '#0D1B3E', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  messages: { flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 4 },
  dateSep: { textAlign: 'center', fontSize: 11, color: '#9CA3AF', margin: '12px 0 8px', fontWeight: 600 },
  msgRow: { display: 'flex', marginBottom: 4 },
  bubble: { maxWidth: '70%', padding: '10px 14px', borderRadius: 16, position: 'relative' },
  bubbleText: { margin: 0, fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  bubbleTime: { fontSize: 10, display: 'block', marginTop: 4, textAlign: 'right' },
  replyBox: { borderTop: '1px solid #E5E7EB', background: '#fff', padding: 16 },
  replyInput: { width: '100%', minHeight: 70, padding: '10px 12px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box' },
  replyFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 },
  replyHint: { fontSize: 11, color: '#9CA3AF' },
  sendBtn: { background: '#E8920A', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  noCaseNote: { fontSize: 13, color: '#9CA3AF', textAlign: 'center', padding: '12px 0' },
  empty: { fontSize: 13, color: '#9CA3AF', textAlign: 'center', padding: '20px 0' },
};
